import * as React from "react";
import * as Sentry from "@sentry/react";
import { useToggle } from "react-use";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FolderItem } from "@/types/fse";
import { useUnwrapFolderMutation } from "@/api/files";
import { toast } from "react-toastify";
import * as analytics from "@/services/analytics";
import { formatFolderContentsCount } from "@/views/drive/components/driverRowName/folderCounts";
import { formatUnwrapSummary } from "@/views/drive/modals/unwrapFolderModal/helpers";

interface Props {
  item: FolderItem;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  onUnwrap?: () => void;
}

export const UnwrapFolderModal: React.FC<Props> = ({ isOpen, setIsOpen, item, onUnwrap }) => {
  const [error, setError] = React.useState("");
  const [isProcessing, toggleIsProcessing] = useToggle(false);
  const [unwrapFolder] = useUnwrapFolderMutation();

  const contents = formatFolderContentsCount(item);

  const handleUnwrap = async () => {
    toggleIsProcessing(true);
    setError("");

    try {
      const result = await unwrapFolder({ fse_id: item.id }).unwrap();
      analytics.folderUnwrap(item.id, item.name, result.moved_count);
      setIsOpen(false);
      toast.success(`"${item.name}" unwrapped — ${formatUnwrapSummary(result)}.`);
      onUnwrap?.();
    } catch (error) {
      setError("Failed to unwrap the folder. Please try again.");
      Sentry.captureException(error);
    } finally {
      toggleIsProcessing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Unwrap folder</DialogTitle>
          <DialogDescription>
            Everything inside &quot;{item.name}&quot;{contents && contents !== "Empty" ? ` (contains ${contents})` : ""}{" "}
            moves into the folder above it, and &quot;{item.name}&quot; itself is deleted. Items whose names clash with
            something already there are renamed, and documents already in the destination are skipped. Anyone the folder
            itself was shared with loses access to its contents. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        {error && <p className="text-red-500">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleUnwrap} disabled={isProcessing}>
            {isProcessing ? "Unwrapping..." : "Unwrap folder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
